import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Target, TrendingUp } from 'lucide-react';
import { Badge } from '../ui/badge';
import { getDifficultyLabel } from '../../utils/constants';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

export const AdminOverviewTab = ({ stats, registrationData }) => {
  const sortedGoals = useMemo(() => 
    Object.entries(stats?.popular_goals || {}).sort((a, b) => b[1] - a[1]),
    [stats?.popular_goals]
  );
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Experience Level Breakdown */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border p-6"
      >
        <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">
          <BarChart3 className="inline h-5 w-5 mr-2 text-primary" />
          Experience Levels
        </h3>
        <div className="space-y-4">
          {['beginner', 'intermediate', 'advanced'].map(level => {
            const count = stats?.experience_breakdown?.[level] || 0;
            const percentage = stats?.total_users > 0 ? (count / stats.total_users) * 100 : 0;
            return (
              <div key={level}>
                <div className="flex justify-between mb-1">
                  <span className="text-white">{getDifficultyLabel(level)}</span>
                  <span className="text-muted-foreground">{count} users</span>
                </div>
                <div className="h-2 bg-black rounded overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Popular Goals */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-card border border-border p-6"
      >
        <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">
          <Target className="inline h-5 w-5 mr-2 text-primary" />
          Popular Goals
        </h3>
        <div className="space-y-3">
          {sortedGoals.map(([goal, count]) => (
              <div key={goal} className="flex items-center justify-between p-3 bg-black border border-border">
                <span className="text-white">{goalLabels[goal] || goal}</span>
                <Badge className="bg-primary/20 text-primary">{count} users</Badge>
              </div>
            ))}
        </div>
      </motion.div>

      {/* Recent Registrations Chart */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card border border-border p-6 md:col-span-2"
      >
        <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">
          <TrendingUp className="inline h-5 w-5 mr-2 text-primary" />
          Registration Trend (Last 30 Days)
        </h3>
        <div className="flex items-end gap-1 h-40">
          {(Array.isArray(registrationData) ? registrationData : []).slice(-30).map((day, i) => {
            const maxCount = Math.max(...(Array.isArray(registrationData) ? registrationData : []).map(d => d.count), 1);
            const height = (day.count / maxCount) * 100;
            return (
              <div 
                key={day.date || i}
                className="flex-1 bg-primary/20 hover:bg-primary/40 transition-colors relative group"
                style={{ height: `${Math.max(height, 5)}%` }}
              >
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black px-2 py-1 text-xs text-white whitespace-nowrap border border-border">
                    {day.date}: {day.count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};
